import {Pages} from 'shared/config/model/pages';
import {AppUrls} from 'shared/config/model/urls';

import React, {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {i18n} from '@lingui/core';
import {t} from '@lingui/core/macro';

import {PAYMENT_FILTER_KEYS, paymentAPI, PaymentsFiltersForm} from 'entities/payment';
import ListPage from 'shared/ui/ListPage/ListPage';
import ListToolbar from 'shared/ui/ListToolbar/ListToolbar';
import TableToolbar from 'shared/ui/TableToolbar/TableToolbar';
import ListFiltersModal from 'shared/ui/Modal/ListFiltersModal/ListFiltersModal';
import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import {getPollingInterval} from 'shared/api/pollingHelper';
import useOpenState from 'shared/lib/hooks/useOpenState';
import useListParams from 'shared/lib/hooks/useListParams';
import useResetPageOnEmpty from 'shared/lib/hooks/useResetPageOnEmpty';
import {userSelector} from 'entities/user';

import PaymentRow from './PaymentRow';
import PaymentsPlug from './PaymentsPlug/PaymentsPlug';
import AddAutoRenewalModal from './AddAutoRenewalModal/AddAutoRenewalModal';
import DisableAutoRenewalModal from './DisableAutoRenewalModal/DisableAutoRenewalModal';

const Payments: React.FC = () => {
    const [isAddAutoRenewalModalOpen, setAddAutoRenewalModalOpen] = useState(false);
    const [isDisableAutoRenewalModalOpen, setDisableAutoRenewalModalOpen] = useState(false);
    const {args, sort, setSort, hasFilters, search, onSearch} = useListParams({filterKeys: PAYMENT_FILTER_KEYS});
    const user = useSelector(userSelector);
    const filtersModal = useOpenState();

    const {data: pagination, isLoading, refetch, isFetching} = paymentAPI.useGetPaymentsQuery(args, {
        pollingInterval: getPollingInterval(),
    });
    const isRedirecting = useResetPageOnEmpty(pagination);

    const handleToggleClick = useCallback(() => {
        const currentState = !!user?.is_recurring;

        if (currentState) {
            setDisableAutoRenewalModalOpen(true);
        } else {
            setAddAutoRenewalModalOpen(true);
        }
    }, [user?.is_recurring]);

    return (
        <ListPage
            documentTitle={`${i18n._(Pages.PAYMENTS)} | ${t`Overseer`}`}
            toolbar={
                <ListToolbar
                    title={i18n._(Pages.PAYMENTS)}
                    backUrl={AppUrls.SETTINGS}
                    summary={
                        <StatusToggle
                            condition={!!user?.is_recurring}
                            textActive={t`Автопродление включено`}
                            notActiveText={t`Автопродление отключено`}
                            errorToggle
                            onClick={handleToggleClick}
                            preventToggle
                        />
                    }
                    actions={
                        <TableToolbar
                            onSearch={onSearch}
                            searchValue={search}
                            onFilter={filtersModal.open}
                            onRefresh={refetch}
                            isRefreshing={isFetching}
                        />
                    }
                />
            }
            plug={!hasFilters && pagination?.totalItems === 0 && <PaymentsPlug />}
            columns={[
                {label: t`№`, sort: 'number'},
                {label: t`Дата создания`, sort: 'created_at'},
                {label: t`Сумма`, sort: 'sum'},
                {label: t`Тариф`, sort: 'tariff.name'},
                {label: t`Вид оплаты`, sort: 'auto'},
                {label: t`Статус`, sort: 'status'},
                {label: t`Дата оплаты`, sort: 'payed_at'},
            ]}
            items={pagination?.items}
            rowFactory={item => <PaymentRow
                key={item.number}
                item={item}
                tzString={user?.timezone}
            />}
            isLoading={isLoading || isRedirecting}
            pagination={pagination}
            sort={sort}
            setSort={setSort}
            emptyStateComponent={hasFilters ? t`Ничего не найдено` : true}
        >
            <AddAutoRenewalModal
                isOpen={isAddAutoRenewalModalOpen}
                handleClose={() => setAddAutoRenewalModalOpen(false)}
            />

            <DisableAutoRenewalModal
                item={user}
                isOpen={isDisableAutoRenewalModalOpen}
                handleClose={() => setDisableAutoRenewalModalOpen(false)}
            />
            <ListFiltersModal
                open={filtersModal.isOpen}
                handleClose={filtersModal.close}
                Form={PaymentsFiltersForm}
            />
        </ListPage>
    );
};

export default Payments;